/**
 * Economy store - Feature-first Zustand store for the virtual coin economy.
 * Owns the authoritative client-side coin balance and daily-claim state.
 */

import { create } from 'zustand';
import { API_ENDPOINTS } from '../../../constants/api';

function extractApiErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const maybeAxiosError = error as {
    response?: {
      data?: {
        message?: unknown;
        error?: unknown;
      };
    };
    message?: unknown;
  };

  const responseMessage = maybeAxiosError.response?.data?.message;
  if (typeof responseMessage === 'string' && responseMessage.trim().length > 0) {
    return responseMessage;
  }

  const responseError = maybeAxiosError.response?.data?.error;
  if (typeof responseError === 'string' && responseError.trim().length > 0) {
    return responseError;
  }

  return '';
}

function getNextUTCMidnightISO(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)).toISOString();
}

interface EconomyState {
  coins: number;
  /**
   * All-time-high coin balance for the coin-based rank ladder (specs/coin-based-ranks). Ratchets
   * up only: every coin raise (`syncCoins`, claim, coupon) lifts it; spends/losses leave it.
   * Hydrated from the server (`syncPeakCoins`) so a spent-down user keeps their earned rank.
   */
  peakCoins: number;
  /** False until a trusted balance has been synced at least once. Lets displays show a
   *  placeholder instead of a misleading "0" while the profile fetch is in flight or failed. */
  coinsLoaded: boolean;
  isClaiming: boolean;
  isRedeemingCoupon: boolean;
  /** ISO string of next UTC midnight (null = not yet claimed today / unknown). */
  nextClaimAt: string | null;
  /** Current streak in the 7-day ladder (1–7). */
  dailyClaimStreak: number;
  /** Last successful claim timestamp in ISO format. */
  lastDailyClaimAt: string | null;
  /** Sync coins from a trusted source (e.g. profile fetch). Also ratchets `peakCoins` up. */
  syncCoins: (amount: number) => void;
  /** Hydrate the all-time-high coin balance from the server; only ever raises `peakCoins`. */
  syncPeakCoins: (amount: number) => void;
  /** Hydrate claim state from profile load; sets nextClaimAt if already claimed today. */
  syncClaimState: (streak: number, lastDailyClaimAt: string | null) => void;
  /** Clear local economy state when user signs out. */
  resetEconomy: () => void;
  /** Claim daily coins via the backend. Requires an authenticated apiClient function. */
  claimDaily: (apiClient: (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>) => Promise<{ success: boolean; claimed?: number; streak?: number; nextDayCoins?: number; error?: string; retryAfterMs?: number }>;
  /** Redeem a promo coupon for coins. Code is case-sensitive (sent as-is). */
  redeemCoupon: (
    apiClient: (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>,
    code: string,
  ) => Promise<{ success: boolean; awarded?: number; coins?: number; error?: string }>;
}

export const useEconomyStore = create<EconomyState>((set, get) => ({
  coins: 0,
  peakCoins: 0,
  coinsLoaded: false,
  isClaiming: false,
  isRedeemingCoupon: false,
  nextClaimAt: null,
  dailyClaimStreak: 0,
  lastDailyClaimAt: null,

  syncCoins: (amount: number) => {
    const state = get();
    // Mark loaded on first sync even when amount === current (e.g. a genuine 0-coin user),
    // so the balance display leaves its placeholder state.
    const peakCoins = Math.max(state.peakCoins, amount);
    if (amount !== state.coins || !state.coinsLoaded || peakCoins !== state.peakCoins) {
      set({ coins: amount, coinsLoaded: true, peakCoins });
    }
  },

  syncPeakCoins: (amount: number) => {
    const state = get();
    const peakCoins = Math.max(state.peakCoins, amount);
    if (peakCoins !== state.peakCoins) set({ peakCoins });
  },

  syncClaimState: (streak: number, lastDailyClaimAt: string | null) => {
    let nextClaimAt: string | null = null;
    if (lastDailyClaimAt) {
      const d = new Date(lastDailyClaimAt);
      const now = new Date();
      const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
      const lastClaimDayUTC = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      if (lastClaimDayUTC >= todayUTC) {
        nextClaimAt = getNextUTCMidnightISO();
      }
    }
    set({ dailyClaimStreak: streak, nextClaimAt, lastDailyClaimAt });
  },

  resetEconomy: () => {
    set({
      coins: 0,
      peakCoins: 0,
      coinsLoaded: false,
      isClaiming: false,
      isRedeemingCoupon: false,
      nextClaimAt: null,
      dailyClaimStreak: 0,
      lastDailyClaimAt: null,
    });
  },

  claimDaily: async (apiClient) => {
    if (get().isClaiming) return { success: false, error: 'Already claiming' };
    set({ isClaiming: true });

    try {
      const response = await apiClient(API_ENDPOINTS.users.dailyClaim, { method: 'POST' });
      const body = response.data as {
        success?: boolean;
        data?: { coins?: number; claimed?: number; streak?: number; nextDayCoins?: number };
        error?: string;
        retryAfterMs?: number;
      };

      if (response.status >= 400 || !body.success) {
        if (typeof body.retryAfterMs === 'number') {
          set({ nextClaimAt: new Date(Date.now() + body.retryAfterMs).toISOString() });
        }
        return {
          success: false,
          error: body.error ?? 'Claim failed',
          retryAfterMs: body.retryAfterMs,
        };
      }

      const newBalance = body.data?.coins;
      const streak = body.data?.streak ?? 1;
      // Ratchet the rank high-water mark when a claim raises the balance (specs/coin-based-ranks
      // AC-3/AC-7): the daily claim earns no XP, so this is the only signal the rank can rise.
      const peakCoins = typeof newBalance === 'number'
        ? Math.max(get().peakCoins, newBalance)
        : get().peakCoins;
      set({
        ...(typeof newBalance === 'number' && { coins: newBalance, coinsLoaded: true, peakCoins }),
        nextClaimAt: getNextUTCMidnightISO(),
        dailyClaimStreak: streak,
        lastDailyClaimAt: new Date().toISOString(),
      });

      return {
        success: true,
        claimed: body.data?.claimed,
        streak,
        nextDayCoins: body.data?.nextDayCoins,
      };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (
        msg === 'No active session' ||
        msg === 'Authentication token not available' ||
        msg === 'AUTH_TIMEOUT'
      ) {
        return { success: false, error: 'SIGN_IN_REQUIRED' };
      }
      return { success: false, error: 'Network error' };
    } finally {
      set({ isClaiming: false });
    }
  },

  redeemCoupon: async (apiClient, code) => {
    if (get().isRedeemingCoupon) return { success: false, error: 'Already redeeming' };
    set({ isRedeemingCoupon: true });

    try {
      const response = await apiClient(API_ENDPOINTS.users.redeemCoupon, {
        method: 'POST',
        data: { code },
      });
      const body = response.data as {
        success?: boolean;
        data?: { coins?: number; awarded?: number };
        error?: string;
      };

      if (response.status >= 400 || !body.success) {
        return {
          success: false,
          error: body.error ?? 'Redeem failed',
        };
      }

      const newBalance = body.data?.coins;
      const awarded = body.data?.awarded;
      if (typeof newBalance === 'number') {
        // Coupon coins earn no XP → ratchet the rank high-water mark here too (AC-3/AC-7).
        set({ coins: newBalance, coinsLoaded: true, peakCoins: Math.max(get().peakCoins, newBalance) });
      }

      return {
        success: true,
        ...(typeof awarded === 'number' && { awarded }),
        ...(typeof newBalance === 'number' && { coins: newBalance }),
      };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (
        msg === 'No active session' ||
        msg === 'Authentication token not available' ||
        msg === 'AUTH_TIMEOUT'
      ) {
        return { success: false, error: 'SIGN_IN_REQUIRED' };
      }
      const apiMsg = extractApiErrorMessage(err);
      if (apiMsg) {
        return { success: false, error: apiMsg };
      }
      return { success: false, error: 'Network error' };
    } finally {
      set({ isRedeemingCoupon: false });
    }
  },
}));
